import React, { useRef } from "react";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { darkBg, gridColor, labelColor, makeGradient, tooltipStyle } from "../api/echartsTheme";
import GraphDownloadButton from "../../Global/other/GraphDownloadButton";
import { formatSAST } from "../../Global/other/formatSAST";

const colorAssessment = "#FDB940";
const colorBaseline = "#94ADFF";

export default function MunicipalityGraphGraph({
  municipalityGraphData,
  isSuccessMunicipalityGraphData,
  isErrorMunicipalityGraphData,
  errorMessageMunicipalityGraphData,
  isLoadingMunicipalityGraphData,
  startDate,
  endDate,
}) {
  const chartRef = useRef(null);

  if (isLoadingMunicipalityGraphData) {
    return (
      <Card variant="outlined" sx={{ mt: 2, backgroundColor: darkBg, p: 3 }}>
        <CircularProgress size={20} sx={{ color: colorAssessment }} />
      </Card>
    );
  }

  if (isErrorMunicipalityGraphData) {
    return (
      <Card variant="outlined" sx={{ mt: 2, backgroundColor: darkBg, p: 3 }}>
        <Typography sx={{ color: "#e05050" }}>
          Error: {errorMessageMunicipalityGraphData?.data?.error_message}
        </Typography>
      </Card>
    );
  }

  if (!isSuccessMunicipalityGraphData) return <></>;

  const assessmentData = (municipalityGraphData?.assessment_plot || []).map((p) => [
    new Date(p.x).getTime(),
    p.y,
  ]);
  const baselineData = (municipalityGraphData?.baseline_plot || []).map((p) => [
    new Date(p.x).getTime(),
    p.y,
  ]);

  const option = {
    backgroundColor: darkBg,
    animation: false,
    grid: { top: 16, right: 24, bottom: 76, left: 72 },
    dataZoom: [
      { type: "inside", xAxisIndex: 0, filterMode: "filter" },
      {
        type: "slider",
        xAxisIndex: 0,
        height: 18,
        bottom: 32,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderColor: "rgba(255,255,255,0.07)",
        fillerColor: "rgba(253,185,64,0.2)",
        handleStyle: { color: "#FDB940", borderColor: "#FDB940" },
        moveHandleStyle: { color: "#FDB940" },
        emphasis: { handleStyle: { color: "#FDB940" } },
        textStyle: { color: "transparent" },
        brushSelect: false,
        showDetail: false,
      },
    ],
    legend: { show: false },
    tooltip: {
      trigger: "axis",
      ...tooltipStyle,
      formatter: (params) =>
        params
          .map((p) => `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${p.color};margin-right:6px"></span>${p.seriesName}: <b>${Number(p.value[1]).toFixed(3)}</b>`)
          .join("<br/>"),
    },
    xAxis: {
      type: "time",
      axisLine: { lineStyle: { color: gridColor } },
      axisTick: { lineStyle: { color: gridColor } },
      axisLabel: {
        color: labelColor,
        fontSize: 10,
        formatter: (v) => {
          const d = new Date(v);
          return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
        },
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: "value",
      name: "MVA",
      nameTextStyle: { color: labelColor, fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: labelColor, fontSize: 10, formatter: (v) => v.toFixed(2) },
      splitLine: { lineStyle: { color: gridColor, type: "dashed" } },
    },
    series: [
      {
        name: "Assessment",
        type: "line",
        smooth: false,
        symbol: "none",
        lineStyle: { color: colorAssessment, width: 2 },
        areaStyle: { color: makeGradient(colorAssessment), opacity: 0.3 },
        itemStyle: { color: colorAssessment },
        data: assessmentData,
      },
      {
        name: "Baseline",
        type: "line",
        smooth: false,
        symbol: "none",
        lineStyle: { color: colorBaseline, width: 2 },
        areaStyle: { color: makeGradient(colorBaseline), opacity: 0.3 },
        itemStyle: { color: colorBaseline },
        data: baselineData,
      },
    ],
  };

  const handleDownloadCsv = () => {
    if (!municipalityGraphData?.baseline_plot) return;
    const fname = `MV_${startDate?.format("YYYY-MM-DD")}_to_${endDate?.format("YYYY-MM-DD")}`;
    let csv = "Timestamp (SAST),Baseline (MVA),Assessment (MVA)\r\n";
    municipalityGraphData.baseline_plot.forEach((pt, i) => {
      const dt = formatSAST(pt.x);
      const baseline = pt.y != null ? Number(pt.y).toFixed(4) : "";
      const assessment = municipalityGraphData.assessment_plot[i]?.y != null
        ? Number(municipalityGraphData.assessment_plot[i].y).toFixed(4) : "";
      csv += `${dt},${baseline},${assessment}\r\n`;
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fname}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPng = () => {
    const url = chartRef.current?.getEchartsInstance()?.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: darkBg });
    if (!url) return;
    const fname = `MV_${startDate?.format("YYYY-MM-DD")}_to_${endDate?.format("YYYY-MM-DD")}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fname}.png`;
    a.click();
  };

  return (
    <Card variant="outlined" sx={{ mt: 2, backgroundColor: darkBg }}>
      {/* Card header: title left, download button right */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 2, px: 2 }}>
        <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}>Demand M&amp;V</Typography>
        <GraphDownloadButton onDownloadCsv={handleDownloadCsv} onDownloadPng={handleDownloadPng} />
      </Box>
      <Grid container sx={{ maxWidth: "1700px", mx: "auto" }}>
        <Grid item xs={12} sx={{ p: 1 }}>
          <ReactECharts ref={chartRef} option={option} style={{ height: 450 }} notMerge />
        </Grid>
      </Grid>
      {/* Legend below chart */}
      <Box sx={{ display: "flex", gap: "20px", px: 2, pb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: colorAssessment, borderRadius: 1 }} />
          <Typography sx={{ color: labelColor, fontSize: "0.75rem" }}>Assessment</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Box sx={{ width: 20, height: 2, backgroundColor: colorBaseline, borderRadius: 1 }} />
          <Typography sx={{ color: labelColor, fontSize: "0.75rem" }}>Baseline</Typography>
        </Box>
      </Box>
    </Card>
  );
}
